import { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import useNotify from './useNotify';
import { ucWords } from '@ecosconnect/core/src/helpers/string';
import { gql } from '@/modes/common/gql';

const GET_ASSEMBLY_MAKES_MODELS = gql(`
  query GetbackflowAssemblyMakesAndModels($where: JSON) {
    backflowAssemblyMakes(
      where: $where
      orderBy: [
        { col: "name", dir: ASC }
        { col: "backflowAssemblyModels.name", dir: ASC }
      ]
    ) {
      id
      name
      backflowAssemblyModels {
        id
        backflowAssemblyMakeId
        name
      }
    }
  }
`);

export type Model = {
  id: string;
  backflowAssemblyMakeId: string;
  name: string;
};

export type Make = {
  id: string;
  name: string;
  backflowAssemblyModels: Model[];
};

const useMakesAndModels = () => {
  const [makes, setMakes] = useState<Make[] | null>(null);
  const [makesByModel, setMakesByModel] = useState<Record<string, Make> | null>(
    null
  );
  const [makeNames, setMakeNames] = useState<Record<string, string> | null>(
    null
  );
  const [models, setModels] = useState<Model[] | null>(null);
  const [modelNames, setModelNames] = useState<Record<string, string> | null>(
    null
  );
  const [modelsByMake, setModelsByMake] = useState<Record<
    string,
    Model[]
  > | null>(null);

  const { data, loading, error } = useQuery<{
    backflowAssemblyMakes: Make[];
  }>(GET_ASSEMBLY_MAKES_MODELS);

  const { showError } = useNotify();

  useEffect(() => {
    if (!loading && error) {
      showError('Error loading makes and models', error);
    }
  }, [loading, error]);

  useEffect(() => {
    if (data && data.backflowAssemblyMakes?.length) {
      const models: Model[] = [];
      const modelsByMakeObj: Record<string, Model[]> = {};
      const modelNamesObj: Record<string, string> = {};
      const makesByModelObj: Record<string, Make> = {};
      const makesNamesObj: Record<string, string> = {};
      const makes: Make[] = [];

      data.backflowAssemblyMakes.forEach((make) => {
        make = {
          ...make,
        };
        make.name = ucWords(make.name);
        makesNamesObj[make.id] = make.name;
        makes.push(make);
        make.backflowAssemblyModels.forEach((model) => {
          model = {
            ...model,
          };
          model.name = ucWords(model.name);
          models.push(model);
          modelNamesObj[model.id] = model.name;
          makesByModelObj[model.id] = make;
        });
        modelsByMakeObj[make.id] = make.backflowAssemblyModels;
      });

      setMakes(makes);
      setMakeNames(makesNamesObj);
      setMakesByModel(makesByModelObj);
      setModels(models);
      setModelNames(modelNamesObj);
      setModelsByMake(modelsByMakeObj);
    }
  }, [data]);

  return {
    makes,
    makeNames,
    makesByModel,
    models,
    modelNames,
    modelsByMake,
    loading,
    error,
    data,
  };
};

export default useMakesAndModels;
